import createChainedFunction from 'utils/createChainedFunction';
import invariant from 'invariant';
import PropTypes from 'prop-types';
import * as React from 'react';
import { MouseEventHandler } from 'types/typescript/eventHandlers';
import ErrorReporter from 'app/components/ErrorReporter';

// @ts-ignore
import userAgent from 'lib/currentUserAgent';

/**
 * ## Copies text to the clipboard.
 *
 *
 * # Common usage
 *
 * ```example
 * <ClipboardTrigger
 *   text="https://www.remind.com"
 *   onCopy={() => window.alert('Link copied!')}>
 *   <Button>
 *     Copy text to clipboard
 *   </Button>
 * </ClipboardTrigger>
 * ```
 *
 * # Required props
 *
 * ```example
 * <ClipboardTrigger text="https://www.remind.com">
 *   <Button>
 *     Copy text to clipboard
 *   </Button>
 * </ClipboardTrigger>
 * ```
 *
 *
 * # TODO
 * * Enforce using `focus`-able child
 */

type Props = {
  text: string;
  onClick?: () => void;
  children?: React.ReactElement<any>;
} & DefaultProps;

type DefaultProps = {
  onCopy: () => any;
};

class ClipboardTrigger extends React.Component<Props> {
  static defaultProps: DefaultProps = {
    onCopy: () => {},
  };

  handleClick: MouseEventHandler = event => {
    event.preventDefault();
    invariant(document.body, 'null document.body in ClipboardTrigger handleClick');
    const { body } = document;

    const input = document.createElement('input');
    input.readOnly = true;
    input.style.cssText = `
      position: absolute;
      top: -9999px;
    `;
    input.value = this.props.text;
    invariant(document.body, 'null document.body in ClipboardTrigger handleClick');
    body.appendChild(input);
    input.select();

    try {
      const success = document.execCommand('copy');
      body.removeChild(input);
      invariant(success, 'unable to copy');
      this.props.onCopy();
    } catch (e) {
      const message = userAgent.mac()
        ? 'Press Cmd-C to copy to clipboard'
        : 'Press Control-C to copy to clipboard';

      // eslint-disable-next-line no-alert
      if (window.prompt(message, this.props.text)) {
        this.props.onCopy();
      }
    }
  };
  render() {
    const { children, onClick } = this.props;

    if (React.Children.count(children) === 1) {
      const trigger = React.Children.only(children);

      if (!trigger) {
        return null;
      }

      return React.cloneElement(trigger, {
        onClick: createChainedFunction(this.handleClick, onClick),
      });
    } else {
      return <ErrorReporter error="ClipboardTrigger - tried to render without exactly one child" />;
    }
  }
}

export default ClipboardTrigger;
